import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DollarSign, ShoppingBag, Users, TrendingUp, LifeBuoy, Mail, AlertTriangle, Repeat } from "lucide-react";

const StatCard = ({ icon: I, label, value, sub }) => (
  <div className="border border-slate-200 rounded-md p-5 bg-white" data-testid={`admin-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="w-9 h-9 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center">
      <I className="w-4 h-4 text-blue-700" strokeWidth={1.75}/>
    </div>
    <div className="mt-5 font-display text-3xl font-semibold text-slate-900">{value}</div>
    <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    {sub && <div className="text-[11px] text-slate-400 mt-1">{sub}</div>}
  </div>
);

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div className="text-slate-400 font-mono text-xs uppercase tracking-widest">Loading stats…</div>;

  return (
    <div data-testid="admin-overview">
      <div className="mb-8">
        <div className="eyebrow mb-2">Admin</div>
        <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Overview.</h1>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={DollarSign} label="Total Revenue" value={`$${stats.total_revenue.toLocaleString()}`} />
        <StatCard icon={ShoppingBag} label="Orders" value={stats.total_orders} />
        <StatCard icon={Users} label="Customers" value={stats.total_customers} />
        <StatCard icon={TrendingUp} label="Avg Order Value" value={`$${stats.average_order_value}`} />
        <StatCard icon={Repeat} label="Active Subs" value={stats.active_subscriptions} sub="Phase 2" />
        <StatCard icon={LifeBuoy} label="Open Tickets" value={stats.open_tickets} />
        <StatCard icon={Mail} label="Newsletter" value={stats.newsletter_subscribers} />
        <StatCard icon={AlertTriangle} label="Low Stock" value={stats.low_stock_alerts} />
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-slate-200 rounded-md bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold text-slate-900">Sales by month</h2>
            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Last 6 months</span>
          </div>
          {stats.sales_by_month.length === 0 ? (
            <div className="text-sm text-slate-500">No sales data yet.</div>
          ) : (
            <div className="space-y-2">
              {stats.sales_by_month.map((s) => {
                const max = Math.max(...stats.sales_by_month.map(x => x.revenue));
                const pct = max > 0 ? (s.revenue / max) * 100 : 0;
                return (
                  <div key={s.month}>
                    <div className="flex justify-between text-xs text-slate-600 mb-1">
                      <span>{s.month}</span><span className="font-mono">${s.revenue.toLocaleString()}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-md overflow-hidden">
                      <div className="h-full bg-blue-500" style={{ width: `${pct}%` }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="border border-slate-200 rounded-md bg-white p-6">
          <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">Top products</h2>
          {stats.top_products.length === 0 ? (
            <div className="text-sm text-slate-500">No product sales yet.</div>
          ) : (
            <div className="space-y-2">
              {stats.top_products.map((p, i) => (
                <div key={p.name} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-b-0">
                  <div className="flex items-center gap-3">
                    <div className="font-mono text-xs text-slate-400 w-6">#{i+1}</div>
                    <div className="text-sm text-slate-900">{p.name}</div>
                  </div>
                  <div className="font-mono text-sm text-slate-600">{p.quantity} units</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
